import React, { forwardRef } from 'react';
import * as Core from '../../core';
import { kebabCase } from 'lodash';
import { AccessControlWrapper } from '../AccessControlWrapper';
import { FAIcon } from '../fa-icon';
import { InfoTooltip } from '../InfoTooltip';
import { formatClassName, pickDataAttributes } from '../../utils';
import { DataAttributes } from '../../utils/pickDataAttributes';
import { Props } from './types';

/** renders a button */
export const Button = forwardRef<HTMLButtonElement, Props>((props, forwardedRef) => {
  const {
    block,
    branded,
    children,
    className,
    colorScheme = 'brand',
    cssStyle,
    data = {},
    disabled,
    download,
    fontTheme,
    href,
    icon,
    iconFamily,
    iconProps,
    label,
    onClick,
    permissions,
    requiredPermission,
    rightIcon,
    rightIconProps,
    style = 'primary',
    size = 'md',
    tooltipAttributes,
    variant,
    ...additionalProps
  } = props;

  /**
   * Methods
   */
  const onInternalClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (additionalProps?.target === '_blank') {
      window.open(href, additionalProps?.target, 'noopener,noreferrer');
    } else if (href) {
      window.location.assign(href);
    }
    if (onClick) {
      onClick(event);
    }
  };

  const getIcon = (side: string | undefined, icon?: string, iconProps?: object) => {
    if (!icon || icon === 'none') {
      // Note: tech-debt - the Core.Icon does not handle `none` very well. Needs to be solved later
      return undefined;
    }
    // ADO-2950558: Core Icon is not being displayed correctly in Foundry apps
    // Temporariliy rendering FAIcon instead as we were before
    // return <Core.Icon {...iconProps} iconName={icon} />;
    // @ts-expect-error: the ...iconProps spread will overwrite icon={icon} will write a ticket to come back for this so it can be tested. Leaving this existing behavior as is for now
    return <FAIcon icon={icon} spin={!!icon && icon === 'spinner'} additionalClasses={side} {...iconProps} />;
  };

  /**
   * DOM
   */
  const buttonClassName: string =
    formatClassName([
      fontTheme || 'ui-default',
      className,
      {
        [`btn-${style}-branded`]: branded,
        'btn-block': block,
        'no-label': !label,
      },
    ]) || '';
  const dataProps: { [prop: string]: string | number } = {};
  if (data.action) {
    dataProps['data-action'] = data.action;
  }
  if (data.objectId) {
    dataProps['data-object-id'] = data.objectId;
  }
  if (data.objectType) {
    dataProps['data-object-type'] = data.objectType;
  }
  if (data.componentType) {
    dataProps['data-component-type'] = data.componentType;
  }
  if (href) {
    dataProps['data-href'] = href;
  } else {
    dataProps['data-component-type'] = label ? `${kebabCase(label)}-button` : 'button';
  }
  const dataAttributes: DataAttributes = pickDataAttributes(props);
  const buttonVariant = variant || style; // Make sure variant gets precedence over style for non-legacy buttons
  const accessControlProps = {
    permissions,
    requiredPermission,
    ...additionalProps,
  };

  const downloadProps: any = {};

  if (download) {
    downloadProps.download = download;
    downloadProps.href = href;

    if (!href) {
      console.error(
        "'href' is required when using 'download', considering adding it or removing 'download' if you use onClick to perform the download process."
      );
    }
  }

  const foundryButtonProps = {
    ...additionalProps,
    ...dataProps,
    ...dataAttributes,
    ...downloadProps,
    colorScheme,
    className: buttonClassName,
    isDisabled: disabled,
    variant: buttonVariant,
    style: cssStyle,
    ref: forwardedRef,
    size,
  };

  return (
    <AccessControlWrapper {...accessControlProps}>
      <Core.Button
        leftIcon={getIcon('left', icon, iconProps)}
        rightIcon={getIcon('right', rightIcon, rightIconProps)}
        {...foundryButtonProps}
        onClick={onInternalClick}
      >
        {label ? <span className="btn-text">{label}</span> : children}
      </Core.Button>
      {tooltipAttributes?.tooltip && <InfoTooltip tooltipPosition="top" {...tooltipAttributes} />}
    </AccessControlWrapper>
  );
});

Button.displayName = 'ButtonUDL';
